import { jsx as _jsx } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { createContext, forwardRef, useContext, useId, } from "react";
import { Controller, FormProvider, useFormContext, useFormState, } from "react-hook-form";
import { Label } from "./label";
import { cn } from "../lib/cn";
/**
 * The react-hook-form bridge.
 *
 * The point of this file is that a form field's accessibility wiring is
 * derived, never hand-written. `FormItem` mints one id; `FormLabel`,
 * `FormControl`, `FormDescription` and `FormMessage` all read it out of
 * context and compute `htmlFor` / `id` / `aria-describedby` / `aria-invalid`
 * from it. A consumer that writes `<FormControl><Input /></FormControl>` gets
 * a correctly labelled, correctly described, correctly invalid-flagged control
 * for free — and, crucially, cannot get it wrong by forgetting a prop.
 *
 * Every user-facing string still comes from the app: validation messages are
 * whatever the Zod schema produced, descriptions are children.
 */
/** Re-export of `FormProvider`, so an app never imports RHF for the wrapper. */
export const Form = FormProvider;
const FormFieldContext = createContext(null);
const FormItemContext = createContext(null);
/**
 * A typed `Controller` that also publishes the field name to the subtree, so
 * `FormMessage` can find its error without being told which field it belongs to.
 */
export function FormField(props) {
    return (_jsx(FormFieldContext.Provider, { value: { name: props.name }, children: _jsx(Controller, { ...props }) }));
}
/**
 * The ids and the live field state for the enclosing `FormField` + `FormItem`.
 *
 * Throws rather than degrading: a `FormLabel` outside a `FormField` would
 * silently render an unlinked label, which is precisely the defect this file
 * exists to make impossible.
 */
export function useFormField() {
    const fieldContext = useContext(FormFieldContext);
    const itemContext = useContext(FormItemContext);
    const { getFieldState } = useFormContext();
    if (fieldContext === null) {
        throw new Error("useFormField must be used inside a <FormField>");
    }
    if (itemContext === null) {
        throw new Error("useFormField must be used inside a <FormItem>");
    }
    // `useFormState` (rather than the `formState` off the context) is what
    // subscribes THIS component to THIS field — without it a message only
    // repaints when something else happens to re-render the form.
    const formState = useFormState({ name: fieldContext.name });
    const fieldState = getFieldState(fieldContext.name, formState);
    const { id } = itemContext;
    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
}
export const FormItem = forwardRef(function FormItem({ className, ...props }, ref) {
    const id = useId();
    return (_jsx(FormItemContext.Provider, { value: { id }, children: _jsx("div", { ref: ref, className: cn("flex flex-col gap-2", className), ...props }) }));
});
FormItem.displayName = "FormItem";
export const FormLabel = forwardRef(function FormLabel({ className, ...props }, ref) {
    const { error, formItemId } = useFormField();
    return (_jsx(Label, { ref: ref, "data-error": error ? true : undefined, className: cn(error ? "text-danger" : "", className), htmlFor: formItemId, ...props }));
});
FormLabel.displayName = "FormLabel";
/**
 * Renders its single child, injecting the id and the ARIA wiring. The child is
 * any control that forwards `id` / `aria-*` to a real element — `Input`,
 * `Textarea`, `SelectTrigger`, `Checkbox`, `Switch` all do.
 */
export const FormControl = forwardRef(function FormControl({ ...props }, ref) {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
    return (_jsx(Slot, { ref: ref, id: formItemId, "aria-describedby": error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId, "aria-invalid": error ? true : undefined, ...props }));
});
FormControl.displayName = "FormControl";
export const FormDescription = forwardRef(function FormDescription({ className, ...props }, ref) {
    const { formDescriptionId } = useFormField();
    return (_jsx("p", { ref: ref, id: formDescriptionId, className: cn("text-sm text-subtle", className), ...props }));
});
FormDescription.displayName = "FormDescription";
/**
 * The field's validation message. Renders the react-hook-form error when there
 * is one, its children when there is not, and nothing at all when there is
 * neither — so a form has no reserved empty rows and no layout shift beyond the
 * message itself.
 */
export const FormMessage = forwardRef(function FormMessage({ className, children, ...props }, ref) {
    const { error, formMessageId } = useFormField();
    const body = error?.message === undefined ? children : String(error.message);
    if (body === undefined || body === null || body === "") {
        return null;
    }
    return (_jsx("p", { ref: ref, id: formMessageId, 
        // role="alert" implies assertive live-region announcement and atomic reporting
        // on its own — adding that attribute explicitly alongside it is redundant and,
        // on some screen-reader/browser pairs, causes a double announcement.
        // It is placed BEFORE the spread so a caller can override it deliberately, and it
        // is only ever rendered when there IS a message — the early `return null` above is
        // what keeps this from being an empty live region that announces nothing forever.
        role: "alert", className: cn("text-sm font-medium text-danger", className), ...props, children: body }));
});
FormMessage.displayName = "FormMessage";
//# sourceMappingURL=form.js.map